import React, { useState, useTransition } from 'react';
import { Alert, AlertTitle, Button, Stack, TextField, Tooltip } from '@mui/material';
import { OpenInNew, Send, SupportAgent } from '@mui/icons-material';
import { callAPISync, postMail } from '../../../../utils/api';
import { useAppSelector } from '../../../../store/hooks';
import { SnackbarAlert } from '../../../SnackbarAlert';
import { useLogto } from '@logto/react';

const Feedback: React.FC = () => {
	const logto = useLogto();

	const [subject, setSubject] = useState('');
	const [message, setMessage] = useState('');
	const [success, setSuccess] = useState(false);
	const userDetails = useAppSelector((state) => state.user);

	const [isPending, startTransition] = useTransition();

	const handleSendFeedback = () => {
		startTransition(() => {
			callAPISync(logto, (token) => {
				postMail(
					token,
					'prometheus-support@naimuri.com',
					`PROMETHEUS Feedback Received [${subject}]`,
					`Hi,<br><br>Feedback has been received from user <i>${userDetails.name} (${userDetails.email})</i> Their message is as follows:` +
						`<br>${message}<br>`
				);
			});
		});
		setSubject('');
		setMessage('');
		setSuccess(true);
	};

	return (
		<Stack width={'100%'} padding={1} data-testid={'feedback-tab-content'} gap={2}>
			<Alert severity="info">
				<AlertTitle>Feedback</AlertTitle>
				<Stack gap={1}>
					<>
						The below form allows you to send a non-urgent message, whether it be feedback, a feature request, or something else, to the
						Prometheus team. Any help requests or incident reports should be submitted through the Support Desk or sent to
						prometheus-helpdesk@naimuri.com instead.
					</>
					<Tooltip title="Opens in new tab">
						<Button
							startIcon={<SupportAgent />}
							href={import.meta.env.VITE_HELPDESK_URL}
							target="_blank"
							rel="noreferrer"
							fullWidth
							endIcon={<OpenInNew />}
							variant="contained"
							data-testid={'feedback-glpi-button'}
						>
							Go to GLPI (Support Desk)
						</Button>
					</Tooltip>
				</Stack>
			</Alert>
			<TextField
				variant="outlined"
				fullWidth
				value={subject}
				onChange={(e) => setSubject(e.target.value)}
				helperText={<>The subject line of the message you want to send</>}
				required
				data-testid={`feedback-subject-textfield`}
				label="Subject"
			/>
			<TextField
				multiline
				minRows={4}
				variant="outlined"
				fullWidth
				value={message}
				onChange={(e) => setMessage(e.target.value)}
				helperText={<>The message you want to send</>}
				required
				data-testid={`feedback-message-textfield`}
				label="Message"
				inputProps={{ maxLength: 500 }}
			/>
			<Button
				variant="contained"
				startIcon={<Send />}
				disabled={message === '' || subject === '' || isPending}
				onClick={handleSendFeedback}
				data-testid={'feedback-submit-button'}
			>
				{isPending ? 'Sending...' : 'Send'}
			</Button>
			<SnackbarAlert
				open={success}
				onClose={() => setSuccess(false)}
				severity="success"
				alertMessage="Feedback sent successfully! Thank you!"
			/>
		</Stack>
	);
};

export default Feedback;
